import { type Maybe } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { ParticipationEinzelberatung } from '@adornis/buchungsheld-db/entities/ParticipationEinzelberatung.js';
import { EinzelberatungSlot, type Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime, Interval } from 'luxon';
import { filter, switchMap } from 'rxjs';

@customElement('bh-page-admin-einrichtung-counseling-slotview')
export class BHPageAdminEinrichtungCounselingSlotview extends BuchungsheldLitElement {
  @property({ attribute: 'institution-id' }) institutionID?: string;
  @property({ attribute: false }) einzelberatung = new RXController<Maybe<Einzelberatung>>(this, undefined);

  @state() private readonly _participations = new RXController(
    this,
    this.einzelberatung.observable.pipe(
      filter(Boolean),
      switchMap(einzelberatung =>
        ParticipationEinzelberatung.getParticipationsEinzelberatungByProductID(einzelberatung._id)(
          ParticipationEinzelberatung.allFields,
        ),
      ),
    ),
  );

  override render() {
    if (this.einzelberatung.isLoading || !this.einzelberatung.value || this._participations.isLoading)
      return html` <tr-loader></tr-loader> `;
    return html`
      <tr-flex space="md">
        <bh-breadcrumb
          .items=${[
            {
              content: this._translation.translate('counseling', 'plural'),
              href: `/admin/institution-details/${this.institutionID}/counselings`,
            },
            { content: this.einzelberatung.value.name, active: true },
          ]}
        ></bh-breadcrumb>

        <tr-button @click=${() => this.dispatchEvent(new CustomEvent('close'))}>
          ${this._translation.translate('counseling', 'close_timeslots')}
        </tr-button>
        <tr-flex space="sm">
          ${this.getBlankSlots(this.einzelberatung.value).map(slot => {
            const beraterAvailibilities = this.einzelberatung.value?.slots
              // FIXME: this is to hacky
              .filter(s2 => Interval.fromDateTimes(slot.start, s2.start).length('minutes') < 5);

            const waiting = this._participations.value?.filter(
              participation =>
                participation.timeslotID === slot._id && [ParticipationState.WAITING].includes(participation.state),
            );

            return html`
              <tr-flex>
                ${this._column(
                  html`
                    <tr-text>
                      ${slot.start.toLocaleString(DateTime.TIME_SIMPLE)} bis
                      ${slot.end.toLocaleString(DateTime.TIME_SIMPLE)}
                    </tr-text>
                  `,
                )}
              </tr-flex>
              <tr-card ${css({ width: '100%' })}>
                ${this._header()}
                <x-hr></x-hr>
                ${beraterAvailibilities?.map(availability => {
                  const teilnehmer = this._participations.value?.find(
                    participation =>
                      participation.timeslotID === availability._id &&
                      participation.beraterID === availability.berater?._id &&
                      ParticipationState.IN === participation.state,
                  );

                  return html`
                    <tr-flex horizontal space="sm">
                      ${this._column(html` <tr-text> ${availability.berater?.email} </tr-text> `)}
                      ${this._column(
                        html`
                          <tr-text>
                            ${teilnehmer?.user ? `${teilnehmer.user.firstName} ${teilnehmer.user.lastName}` : '---'}
                          </tr-text>
                        `,
                      )}
                    </tr-flex>
                    <x-hr></x-hr>
                  `;
                })}
                ${(waiting ?? []).length > 0
                  ? html`
                      <tr-text bold>
                        ${this._translation.translate('WORKSHOPS', 'STATES', ParticipationState.WAITING)}
                      </tr-text>
                      <tr-flex space="sm">
                        ${waiting?.map(
                          participation =>
                            html`
                              <tr-text>
                                ${participation.user
                                  ? `${participation.user.firstName} ${participation.user.lastName}`
                                  : '---'}
                              </tr-text>
                            `,
                        )}
                      </tr-flex>
                    `
                  : nothing}
              </tr-card>
            `;
          })}
        </tr-flex>
      </tr-flex>
    `;
  }

  private _header() {
    return html`
      <tr-flex horizontal space="sm">
        ${this._column(html` <tr-text bold> ${this._translation.translate('counseling', 'advisor')} </tr-text> `)}
        ${this._column(html` <tr-text bold> ${this._translation.translate('counseling', 'participant')} </tr-text> `)}
      </tr-flex>
    `;
  }

  private _column(content: Renderable) {
    return html` <tr-flex flex> ${content} </tr-flex> `;
  }

  // TODO: remove code duplication
  private getBlankSlots(doc: Einzelberatung) {
    if (!doc?.slotConfig) return [];
    const start = doc.slotConfig.start;
    // if (!start) return XSnackbar.show(this._translation.translate('counseling', 'err_startdate_required'));
    if (!start) return [];
    const end = doc.slotConfig.end;
    // if (!end) return XSnackbar.show(this._translation.translate('counseling', 'err_enddate_required'));
    if (!end) return [];
    const duration = doc.slotConfig.slotDuration;
    // if (!duration) return XSnackbar.show(this._translation.translate('counseling', 'err_duration_required'));
    if (!duration) return [];

    const slots: EinzelberatungSlot[] = [];

    const createSlot = (start: DateTime) => {
      const end = start.plus({ minutes: duration });
      return new EinzelberatungSlot({ start, end });
    };

    const addSlotsTill = (till: DateTime) => {
      while (true as boolean) {
        const lastSlot = slots.at(-1);
        const slotStart = lastSlot?.end ?? doc.slotConfig!.start;
        const slot = createSlot(slotStart);
        if (slot.end <= till) slots.push(slot);
        else break;
      }
    };

    if (doc.slotConfig.hasLunchBreak) addSlotsTill(doc.slotConfig.startLunch); // before Lunchbreak
    if (doc.slotConfig.hasLunchBreak) slots.push(createSlot(doc.slotConfig.endLunch)); // add first slot after lunchbreak
    addSlotsTill(doc.slotConfig.end); // after Lunchbreak

    return slots;
  }
}
